import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import { ROUTES } from 'Constants';
import useStore from 'Hooks/useStore';
import { useJourneySchedule } from 'Global/Journey/Queries/useJourneySchedule';
import { Journey } from 'Global/Journey/Types/Journey';
import { useStopById } from 'Global/JourneyElements/Hooks/useStopsById';
import { BookingPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { formatLongDateTime } from 'Util/_HumanWritten/DateUtils';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import LinkAsButton from 'Views/Components/_HumanWritten/LinkAsButton/LinkAsButton';
import Skeleton from 'Views/Components/_HumanWritten/Skeleton/Skeleton';
import { ClassLeg, classLegGroups, ClassLegGroupNames } from './classLegGroups';

const groupNames: ClassLegGroupNames = {
	vehicle: <FormattedMessage id="class-booking-summary__vehicle-heading" defaultMessage="Vehicle" />,
	passengers: <FormattedMessage id="class-booking-summary__passengers-heading" defaultMessage="Passengers" />,
	addOns: <FormattedMessage id="class-booking-summary__add-ons-heading" defaultMessage="Add-ons" />,
	other: <FormattedMessage id="class-booking-summary__other-heading" defaultMessage="Other" />,
	alterationFee: <FormattedMessage id="class-booking-summary__alteration-fee" defaultMessage="Alteration fee" />,
	trailer: <FormattedMessage id="class-booking-summary__trailer-heading" defaultMessage="Trailer" />,
	baseFare: band =>
		band ? (
			<FormattedMessage
				id="class-booking-summary__base-fare-band"
				defaultMessage="Base fare ({band})"
				values={{ band }}
			/>
		) : (
			<FormattedMessage id="class-booking-summary__base-fare" defaultMessage="Base fare" />
		),
	extraLength: band =>
		band ? (
			<FormattedMessage
				id="class-booking-summary__extra-length-band"
				defaultMessage="Extra length ({band})"
				values={{ band }}
			/>
		) : (
			<FormattedMessage id="class-booking-summary__extra-length" defaultMessage="Extra length" />
		),
	extraWeight: band =>
		band ? (
			<FormattedMessage
				id="class-booking-summary__extra-weight-band"
				defaultMessage="Extra weight ({band})"
				values={{ band }}
			/>
		) : (
			<FormattedMessage id="class-booking-summary__extra-weight" defaultMessage="Extra weight" />
		),
	extraWidth: <FormattedMessage id="class-booking-summary__extra-width" defaultMessage="Extra width" />,
	removed: band =>
		band ? (
			<FormattedMessage
				id="class-booking-summary__removed-band"
				defaultMessage="Removed ({band})"
				values={{ band }}
			/>
		) : (
			<FormattedMessage id="class-booking-summary__removed" defaultMessage="Removed" />
		),
};

/** `isAlteration` keeps a $0 extra on an edit, where it reads as "no change". */
export const useClassLeg = (bookingInfo?: BookingPricingResult, isAlteration = false): ClassLeg | null =>
	bookingInfo ? classLegGroups(bookingInfo, groupNames, isAlteration) : null;

/** `Departing` / `Returning`. */
export function LegDirection({ departing }: { departing: boolean }) {
	return departing ? (
		<FormattedMessage id="class-booking-summary__departing" defaultMessage="Departing" />
	) : (
		<FormattedMessage id="class-booking-summary__returning" defaultMessage="Returning" />
	);
}

/** `Departing total` / `Returning total`. */
export function LegTotalLabel({ departing }: { departing: boolean }) {
	return departing ? (
		<FormattedMessage id="class-booking-summary__departing-total" defaultMessage="Departing total" />
	) : (
		<FormattedMessage id="class-booking-summary__returning-total" defaultMessage="Returning total" />
	);
}

/** `Vancouver → Nanaimo · 29 Sep 2026, 13:00`, as text — for somewhere a component cannot go. */
export function useLegText(journey?: Journey): string {
	const from = useStopById(journey?.startStopId ?? '')?.location.name ?? '';
	const to = useStopById(journey?.endStopId ?? '')?.location.name ?? '';
	const departure = useJourneySchedule(journey as Journey).data?.departureDateTime;

	if (!journey) {
		return '';
	}
	return departure ? `${from} → ${to} · ${formatLongDateTime(departure)}` : `${from} → ${to}`;
}

/** `Vancouver → Nanaimo`. */
export function LegRoute({ journey, className }: { journey: Journey; className?: string }) {
	const from = useStopById(journey.startStopId)?.location.name;
	const to = useStopById(journey.endStopId)?.location.name;

	return (
		<div className={classNames('class-booking-summary__route', className)}>
			<span>{from}</span>
			<span aria-hidden>→</span>
			<span>{to}</span>
		</div>
	);
}

/**
 * The sailing's date and time. A manager's opens the trip, as it does on the current summary. `plain`
 * is the bare text, for a place the chip would not fit (5a's list of legs, which is itself a button).
 */
export function LegDate({ journey, plain }: { journey: Journey; plain?: boolean }) {
	const store = useStore();
	const schedule = useJourneySchedule(journey);
	const departure = schedule.data?.departureDateTime;

	if (plain) {
		return <span>{departure ? formatLongDateTime(departure) : <Skeleton width="5rem" />}</span>;
	}

	const content = (
		<>
			<Icon name="calendar_today" useMaterialIcons classname="class-booking-summary__date-icon" />
			{!departure && <Skeleton width="5rem" />}
			{departure && (
				<span>
					{store.isManager
						? `${dayjs(departure).format('DD MMM YYYY')}, ${formatStandardTime(departure)}`
						: formatLongDateTime(departure)}
				</span>
			)}
		</>
	);

	if (store.isManager) {
		return (
			<LinkAsButton
				to={ROUTES.FERRY_SCHEDULE_EDIT.url(journey.tripId, 'insights')}
				className="class-booking-summary__date"
			>
				{content}
			</LinkAsButton>
		);
	}

	return <p className="class-booking-summary__date">{content}</p>;
}

/** A leg's lines, headed by group: each fare type, vehicle, trailer, add-ons, other. */
export function LegGroups({ leg, isAlteration, divided }: { leg: ClassLeg; isAlteration: boolean; divided?: boolean }) {
	return (
		<>
			{leg.groups.map(group => (
				<div
					key={group.key}
					className={`class-booking-summary__group${divided ? ' class-booking-summary__group--divided' : ''}`}
					data-test-id={`class-booking-summary-group-${group.key}`}
				>
					<span className="class-booking-summary__group-name">{group.name}</span>
					<div className="class-booking-summary__group-items">
						{group.items.map((item, index) => (
							// eslint-disable-next-line react/no-array-index-key
							<div className="class-booking-summary__row" key={index}>
								<span>{item.label}</span>
								<span>{formatPriceDisplay(item.price, isAlteration)}</span>
							</div>
						))}
					</div>
				</div>
			))}
		</>
	);
}

/**
 * The leg in a line per group — `COMFORT  1 adult, 1 senior / teen` — for a card whose full lines sit
 * behind `More details` (design 11c).
 */
export function LegSummary({ leg }: { leg: ClassLeg }) {
	if (leg.groups.length === 0) {
		return null;
	}

	return (
		<div className="class-booking-summary__summary" data-test-id="class-booking-summary-summary">
			{leg.groups.map(group => (
				<div className="class-booking-summary__summary-row" key={group.key}>
					<span className="class-booking-summary__group-name">{group.name}</span>
					<span>
						{group.items.map((item, index) => (
							// eslint-disable-next-line react/no-array-index-key
							<React.Fragment key={index}>
								{index > 0 && ', '}
								{item.label}
							</React.Fragment>
						))}
					</span>
				</div>
			))}
		</div>
	);
}

/** `Departing total   $302.95`, with the leg's discount beneath where it has one. */
export function LegTotal({
	leg,
	departing,
	isAlteration,
	className,
}: {
	leg: ClassLeg;
	departing: boolean;
	isAlteration: boolean;
	className: string;
}) {
	return (
		<>
			<div
				className={className}
				data-test-id={`class-booking-summary-${departing ? 'departing' : 'returning'}-total`}
			>
				<span>
					<LegTotalLabel departing={departing} />
				</span>
				<span>{formatPriceDisplay(leg.total, isAlteration)}</span>
			</div>
			{leg.discount > 0 && (
				<div className="class-booking-summary__discount">
					<span>
						<FormattedMessage id="class-booking-summary__discount" defaultMessage="Discount" />
					</span>
					<span>{formatPriceDisplay(leg.discount * -1, true)}</span>
				</div>
			)}
		</>
	);
}
